import { createFileRoute, Link, useNavigate, useParams, Navigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { loadStripe, type Stripe as StripeJs } from "@stripe/stripe-js";
import {
  Elements,
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { toast } from "sonner";
import { ArrowLeft, Check, Loader2, Lock, ShieldCheck, Sparkles, Zap, Crown, Rocket, CheckCircle2, Clock, CreditCard } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { PLANS } from "@/lib/plans";
import { createSubscription } from "@/lib/subscription.functions";
import { BaridiMobPaymentCard } from "@/components/BaridiMobPaymentCard";
import { PayPalSubscribeButton } from "@/components/PayPalSubscribeButton";
import baridimobLogo from "@/assets/baridimob.png.asset.json";
import { supabase } from "@/integrations/supabase/client";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useTheme } from "@/hooks/use-theme";

export const Route = createFileRoute("/_authenticated/checkout/$plan")({
  head: () => ({
    meta: [
      { title: "Checkout — Lumvio" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CheckoutPage,
});

const ICONS: Record<string, typeof Sparkles> = {
  starter: Rocket,
  pro: Zap,
  unlimited: Crown,
};

type PlanId = "starter" | "pro" | "unlimited";

function CheckoutPage() {
  const { plan } = useParams({ from: "/_authenticated/checkout/$plan" });
  const { profile } = useAuth();
  const navigate = useNavigate();
  const create = useServerFn(createSubscription);
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith("ar") ? "ar" : "en";
  const isRTL = locale === "ar";

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<StripeJs | null> | null>(null);
  const [initError, setInitError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [method, setMethod] = useState<"card" | "paypal" | "baridimob">("card");
  const [pendingManual, setPendingManual] = useState<null | { id: string; created_at: string }>(null);
  const [pendingChecked, setPendingChecked] = useState(false);
  const [alreadyActive, setAlreadyActive] = useState<null | { currentPeriodEnd: number; cancelAtPeriodEnd: boolean }>(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const checkPending = useCallback(async () => {
    if (!profile?.id) return;
    const { data } = await supabase
      .from("manual_payments")
      .select("id, created_at")
      .eq("user_id", profile.id)
      .eq("plan", plan)
      .eq("status", "pending_review")
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    setPendingManual(data ?? null);
    setPendingChecked(true);
  }, [profile?.id, plan]);

  useEffect(() => { checkPending(); }, [checkPending]);

  const planDef = useMemo(() => PLANS.find((p) => p.id === plan), [plan]);

  const planName = useMemo(() => {
    if (!planDef) return "";
    return t(`pricingPage.plans.${planDef.id}.name`, planDef.name);
  }, [planDef, t]);

  const planTagline = useMemo(() => {
    if (!planDef) return "";
    return t(`pricingPage.plans.${planDef.id}.tagline`, planDef.tagline);
  }, [planDef, t]);

  const planFeatures = useMemo(() => {
    if (!planDef) return [];
    const translated = t(`pricingPage.plans.${planDef.id}.features`, { returnObjects: true }) as string[];
    return Array.isArray(translated) ? translated : planDef.features;
  }, [planDef, t]);

  useEffect(() => {
    if (!planDef || planDef.id === "free") return;
    if (!profile) return;
    if (profile.role !== "freelancer") return;
    if (method !== "card") return;
    if (pendingManual) return;
    if (clientSecret) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await create({ data: { plan: plan as PlanId } });
        if (cancelled) return;
        if ("alreadyActive" in res && res.alreadyActive) {
          setAlreadyActive({ currentPeriodEnd: res.currentPeriodEnd, cancelAtPeriodEnd: res.cancelAtPeriodEnd });
          return;
        }
        if ("clientSecret" in res && res.clientSecret && res.publishableKey) {
          setClientSecret(res.clientSecret);
          setStripePromise(loadStripe(res.publishableKey));
          setReady(true);
        }
      } catch (err) {
        if (cancelled) return;
        setInitError(err instanceof Error ? err.message : t("subscriptionCheckout.failedToStart"));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, profile?.id, method]);

  useEffect(() => {
    if (profile && profile.role !== "freelancer") {
      toast.message(t("subscriptionCheckout.freelancerOnly"), {
        description: t("subscriptionCheckout.freelancerOnlyDesc"),
      });
    }
  }, [profile?.role, t]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!profile) return <div className="py-24 text-center text-muted-foreground">{t("subscriptionCheckout.loading")}</div>;
  if (profile.role !== "freelancer") return <Navigate to="/dashboard" />;
  if (!planDef || planDef.id === "free") return <Navigate to="/plans" />;

  const Icon = ICONS[planDef.id] ?? Sparkles;

  const formatDate = (ts: number) => new Date(ts * 1000).toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
  const formatDateTime = (iso: string) => new Date(iso).toLocaleString(locale, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="relative mx-auto max-w-5xl px-4 py-8 sm:py-12">
      {/* Soft ambient glow */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden" aria-hidden>
        <div className="absolute -top-24 left-1/2 h-[400px] w-[600px] -translate-x-1/2 rounded-full bg-primary/8 blur-[120px]" />
      </div>

      <header className="mb-8 flex items-center justify-between">
        <Button asChild variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
          <Link to="/plans">
            <ArrowLeft className="me-2 h-4 w-4 rtl:rotate-180" />
            {t("subscriptionCheckout.backToPlans")}
          </Link>
        </Button>
        <div className="rounded-full border border-border/50 bg-background/60 p-1 backdrop-blur-md">
          <ThemeToggle />
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-[1fr_1.05fr] lg:gap-8">
        {/* Order summary */}
        <section className="order-1 lg:order-1 rounded-3xl border border-border/50 bg-card p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("subscriptionCheckout.subscribeTo")}</div>
              <div className="text-lg font-semibold leading-tight">Lumvio {planName}</div>
            </div>
          </div>

          <div className="mt-8">
            <div className="flex items-baseline gap-1">
              <span className="text-5xl font-semibold tracking-tight tabular-nums text-foreground">
                ${planDef.price.toFixed(2)}
              </span>
              <span className="text-sm font-medium text-muted-foreground">{t("subscriptionCheckout.perMonth")}</span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{planTagline}</p>
          </div>

          <ul className="mt-6 space-y-3">
            {planFeatures.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                <span className="text-foreground/90">{f}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 rounded-2xl border border-border/60 bg-muted/30 p-5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t("subscriptionCheckout.subtotal")}</span>
              <span className="font-medium tabular-nums">${planDef.price.toFixed(2)}</span>
            </div>
            <div className="mt-2 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t("subscriptionCheckout.billing")}</span>
              <span className="text-muted-foreground">{t("subscriptionCheckout.monthlyCancelAnytime")}</span>
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-border/60 pt-4">
              <span className="font-semibold">{t("subscriptionCheckout.dueToday")}</span>
              <span className="text-lg font-semibold tabular-nums text-foreground">${planDef.price.toFixed(2)}</span>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <TrustBadge icon={ShieldCheck} label={t("subscriptionCheckout.stripeSecured")} />
            <TrustBadge icon={Check} label={t("subscriptionCheckout.cancelAnytime")} />
            <TrustBadge icon={Lock} label={t("subscriptionCheckout.noHiddenFees")} />
          </div>
        </section>

        {/* Payment */}
        <section className="order-2 lg:order-2 flex flex-col rounded-3xl border border-border/50 bg-card p-6 sm:p-8">
          <div className="mb-6">
            <div className="text-lg font-semibold">{t("subscriptionCheckout.paymentDetails")}</div>
            <div className="text-sm text-muted-foreground">
              {t("subscriptionCheckout.signedInAs")} <span className="font-medium text-foreground">{profile.email}</span>
            </div>
          </div>

          {alreadyActive ? (
            <div className="flex flex-1 flex-col items-center justify-center rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.06] p-6 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15">
                <CheckCircle2 className="h-7 w-7 text-emerald-500" />
              </div>
              <h3 className="mt-3 text-lg font-semibold">{t("subscriptionCheckout.alreadyActiveTitle", { plan: planName })}</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {alreadyActive.cancelAtPeriodEnd
                  ? t("subscriptionCheckout.accessEnds", { date: formatDate(alreadyActive.currentPeriodEnd) })
                  : t("subscriptionCheckout.renews", { date: formatDate(alreadyActive.currentPeriodEnd) })}
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                <Button asChild variant="outline" size="sm">
                  <Link to="/invoices">{t("subscriptionCheckout.viewInvoices")}</Link>
                </Button>
                <Button asChild variant="outline" size="sm">
                  <Link to="/plans">{t("subscriptionCheckout.changePlan")}</Link>
                </Button>
                <Button
                  size="sm"
                  disabled={portalLoading}
                  onClick={async () => {
                    setPortalLoading(true);
                    try {
                      const { createBillingPortal } = await import("@/lib/subscription.functions");
                      const r = await createBillingPortal({});
                      if (r?.url) window.location.href = r.url;
                    } catch (e) {
                      toast.error(e instanceof Error ? e.message : t("subscriptionCheckout.failedToStart"));
                      setPortalLoading(false);
                    }
                  }}
                >
                  {portalLoading ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : null}
                  {t("subscriptionCheckout.managePaymentMethod")}
                </Button>
              </div>
            </div>
          ) : pendingChecked && pendingManual ? (
            <div className="flex flex-1 flex-col items-center justify-center rounded-2xl border border-yellow-400/20 bg-yellow-400/[0.06] p-6 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-yellow-400/15">
                <Clock className="h-7 w-7 text-yellow-500" />
              </div>
              <h3 className="mt-3 text-lg font-semibold">{t("subscriptionCheckout.transferSubmitted")}</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("subscriptionCheckout.transferSubmittedDesc", { plan: planName })}
              </p>
              <p className="mt-3 text-xs text-muted-foreground">
                {t("subscriptionCheckout.submittedAt", { date: formatDateTime(pendingManual.created_at) })}
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                <Button asChild variant="outline" size="sm">
                  <Link to="/dashboard">{t("subscriptionCheckout.backToDashboard")}</Link>
                </Button>
                <Button variant="ghost" size="sm" onClick={checkPending}>{t("subscriptionCheckout.refreshStatus")}</Button>
              </div>
            </div>
          ) : (
            <>
              {/* Payment method tabs */}
              <div className="mb-6 flex gap-2 overflow-x-auto pb-1">
                <MethodTab
                  active={method === "card"}
                  onClick={() => setMethod("card")}
                  label={t("subscriptionCheckout.payWithCard")}
                  icon={<CreditCard className="h-4 w-4" />}
                />
                <MethodTab
                  active={method === "paypal"}
                  onClick={() => setMethod("paypal")}
                  label="PayPal"
                  icon={<svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden><path d="M7.5 21.5H4l2.5-15h5.75c3 0 4.9 1.65 4.4 4.6-.55 3.3-3 5.15-6.4 5.15H8.6L7.5 21.5Zm2-8.5h1.35c1.75 0 2.9-.85 3.15-2.45.2-1.25-.55-1.95-2-1.95H10.3L9.5 13Z"/><path opacity=".6" d="M17.5 8.5c.35 3.1-1.95 5.5-5.6 5.5h-1.35l-1.1 7h3.5l.6-3.75h1.85c3.15 0 5.35-1.7 5.85-4.75.35-2.15-.7-3.65-3-4Z"/></svg>}
                />
                <MethodTab
                  active={method === "baridimob"}
                  onClick={() => setMethod("baridimob")}
                  label={t("subscriptionCheckout.payWithBaridiMob")}
                  icon={<img src={baridimobLogo.url} alt="" className="h-4 w-4 rounded-[2px] bg-white p-[1px]" />}
                  accent="baridi"
                />
              </div>

              <div className="flex-1">
                {method === "paypal" && (
                  <PayPalSubscribeButton
                    plan={planDef.id as "starter" | "pro" | "unlimited"}
                    onSuccess={() => navigate({ to: "/dashboard" })}
                  />
                )}

                {method === "baridimob" && (
                  <BaridiMobPaymentCard
                    plan={planDef.id as "starter" | "pro" | "unlimited"}
                    amountCents={Math.round(planDef.price * 100)}
                    userId={profile.id}
                    defaultSenderName={profile.full_name ?? ""}
                    onSubmitted={checkPending}
                  />
                )}

                {method === "card" && (
                  <div className="space-y-4">
                    {initError && (
                      <div className="rounded-xl border border-red-500/30 bg-red-500/8 px-4 py-3 text-sm text-red-400">
                        {initError}
                        <div className="mt-3">
                          <Button size="sm" variant="outline" onClick={() => window.location.reload()}>
                            {t("subscriptionCheckout.tryAgain")}
                          </Button>
                        </div>
                      </div>
                    )}

                    {!ready && !initError && (
                      <div className="space-y-3 py-2">
                        <div className="h-11 w-full animate-pulse rounded-xl bg-muted/50" />
                        <div className="h-11 w-full animate-pulse rounded-xl bg-muted/50" />
                        <div className="h-11 w-2/3 animate-pulse rounded-xl bg-muted/50" />
                        <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" /> {t("subscriptionCheckout.preparingCheckout")}
                        </div>
                      </div>
                    )}

                    {ready && clientSecret && stripePromise && (
                      <Elements
                        key={theme}
                        stripe={stripePromise}
                        options={{
                          clientSecret,
                          appearance: {
                            theme: theme === "dark" ? "night" : "stripe",
                            variables: {
                              colorPrimary: "#7c3aed",
                              colorBackground: theme === "dark" ? "#0b0b12" : "#ffffff",
                              colorText: theme === "dark" ? "#e5e7eb" : "#0f172a",
                              colorTextSecondary: theme === "dark" ? "#9ca3af" : "#64748b",
                              colorDanger: "#ef4444",
                              fontFamily: "Inter, system-ui, sans-serif",
                              borderRadius: "12px",
                              spacingUnit: "4px",
                            },
                            rules: {
                              ".Input": {
                                backgroundColor: theme === "dark" ? "rgba(255,255,255,0.03)" : "#ffffff",
                                border: theme === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0",
                                boxShadow: "none",
                              },
                              ".Input:focus": {
                                border: "1px solid #7c3aed",
                                boxShadow: "0 0 0 3px rgba(124,58,237,0.25)",
                              },
                              ".Tab, .Block": {
                                backgroundColor: theme === "dark" ? "rgba(255,255,255,0.03)" : "#f8fafc",
                                border: theme === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0",
                              },
                              ".Tab--selected": {
                                borderColor: "#7c3aed",
                              },
                              ".Label": { color: theme === "dark" ? "#cbd5e1" : "#334155" },
                            },
                          },
                        }}
                      >
                        <CheckoutForm plan={planName} onSuccess={() => navigate({ to: "/dashboard" })} />
                      </Elements>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function TrustBadge({ icon: Icon, label }: { icon: typeof Check; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border/50 bg-muted/30 px-3 py-1.5 text-xs text-muted-foreground">
      <Icon className="h-3 w-3 text-primary" />
      {label}
    </span>
  );
}

function MethodTab({
  active,
  onClick,
  label,
  icon,
  accent,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  icon: React.ReactNode;
  accent?: "baridi";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all",
        active
          ? accent === "baridi"
            ? "border-yellow-400/40 bg-yellow-400/10 text-foreground"
            : "border-primary/30 bg-primary/10 text-foreground"
          : "border-border/50 bg-transparent text-muted-foreground hover:border-border hover:text-foreground"
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function CheckoutForm({ plan, onSuccess }: { plan: string; onSuccess: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);
  const [wallets, setWallets] = useState<string[]>([]);
  const { t } = useTranslation();

  const confirm = async (method: string) => {
    if (!stripe || !elements) return;
    setError(null);
    setSubmitting(true);
    import("@/lib/posthog").then(({ capturePostHog, PHEvents }) =>
      capturePostHog(PHEvents.PaymentStarted, { plan, method }),
    ).catch(() => {});
    const { error: err, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/pricing?upgraded=1`,
      },
      redirect: "if_required",
    });
    if (err) {
      setError(err.message ?? t("subscriptionCheckout.failedToStart"));
      setSubmitting(false);
      return;
    }
    if (paymentIntent?.status === "succeeded" || paymentIntent?.status === "processing") {
      setSucceeded(true);
      import("@/lib/posthog").then(({ capturePostHog, PHEvents }) =>
        capturePostHog(PHEvents.PaymentCompleted, { plan, method, status: paymentIntent.status }),
      ).catch(() => {});
      toast.success(t("subscriptionCheckout.welcomePlan", { plan }));
      setTimeout(onSuccess, 1600);
      return;
    }
    setSubmitting(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await confirm("stripe");
  };

  if (succeeded) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-emerald-500/20 blur-2xl" />
          <CheckCircle2 className="relative h-14 w-14 text-emerald-500" />
        </div>
        <div className="mt-4 text-lg font-semibold">{t("subscriptionCheckout.paymentSuccessful")}</div>
        <div className="mt-1 text-sm text-muted-foreground">{t("subscriptionCheckout.activatingPlan")}</div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className={wallets.length > 0 ? "space-y-4" : "hidden"}>
        <ExpressCheckoutElement
          options={{
            buttonHeight: 44,
            buttonTheme: { applePay: "black", googlePay: "black" },
            layout: { maxColumns: 2, maxRows: 1, overflow: "auto" },
            paymentMethods: {
              applePay: "auto",
              googlePay: "auto",
              link: "auto",
              amazonPay: "never",
              paypal: "never",
              klarna: "never",
            },
          }}
          onReady={({ availablePaymentMethods }) =>
            setWallets(
              availablePaymentMethods
                ? Object.entries(availablePaymentMethods)
                    .filter(([, v]) => v)
                    .map(([k]) => k)
                : [],
            )
          }
          onConfirm={() => { void confirm("wallet"); }}
        />
        <div className="flex items-center gap-3">
          <span className="h-px flex-1 bg-border/60" />
          <span className="text-xs text-muted-foreground">{t("subscriptionCheckout.orPayWithCard")}</span>
          <span className="h-px flex-1 bg-border/60" />
        </div>
      </div>

      <PaymentElement
        options={{
          layout: "tabs",
          terms: {
            card: "never",
            applePay: "never",
            googlePay: "never",
            cashapp: "never",
            usBankAccount: "never",
            auBecsDebit: "never",
            bancontact: "never",
            ideal: "never",
            sepaDebit: "never",
            sofort: "never",
            paypal: "never",
          },
        }}
      />
      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/8 px-3 py-2 text-sm text-red-400">
          {error}
        </div>
      )}

      <div className="rounded-2xl border border-border/50 bg-muted/30 p-4">
        <div className="flex items-start gap-3">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
            <ShieldCheck className="h-4 w-4" />
          </span>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {t("subscriptionCheckout.mandate")}
          </p>
        </div>
      </div>

      <Button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full h-11 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-lg shadow-primary/20 transition-all active:scale-[0.99]"
      >
        {submitting ? (
          <><Loader2 className="me-2 h-4 w-4 animate-spin" /> {t("subscriptionCheckout.processing")}</>
        ) : (
          <>{t("subscriptionCheckout.subscribePay")}</>
        )}
      </Button>
      <p className="text-center text-xs text-muted-foreground">
        {t("subscriptionCheckout.termsAgree")}
      </p>
    </form>
  );
}
